import React from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

interface Props {
  children: React.ReactNode;
  discardLabel?: string;
  hasDiscardableChanges?: boolean;
  hasUnsavedChanges?: boolean;
  isSaving?: boolean;
  onDiscard?: () => void;
  tabName?: string;
}

export const CONTENT_ACTION_BUTTON_SX = {
  minHeight: 36,
  px: 2,
  whiteSpace: 'nowrap',
};

export default function ContentActionBar({
  children,
  discardLabel = 'Discard',
  hasDiscardableChanges,
  hasUnsavedChanges = false,
  isSaving = false,
  onDiscard,
  tabName,
}: Props) {
  const canDiscard = hasDiscardableChanges ?? hasUnsavedChanges;
  const showConfirmDialog = useConfirmDialog();

  const handleDiscard = async () => {
    if (!onDiscard) {
      return;
    }

    const confirmed = await showConfirmDialog({
      title: 'Discard changes?',
      description: 'Any unsaved content changes will be lost.',
    });

    if (confirmed) {
      onDiscard();
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: {
          xs: 16,
          lg: 'max(var(--offer-builder-action-left), calc(50% - 768px + var(--offer-builder-action-left)))',
        },
        right: { xs: 16, lg: 'max(56px, calc(50% - 768px + 56px))' },
        zIndex: 3,
        px: 3,
        py: 1.5,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: (theme) => `0 10px 32px ${alpha(theme.palette.common.black, 0.12)}`,
        backdropFilter: 'blur(8px)',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: hasUnsavedChanges ? 'primary.main' : 'text.disabled',
              flexShrink: 0,
              boxShadow: (theme) =>
                hasUnsavedChanges ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
            }}
          />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {hasUnsavedChanges ? 'Unsaved changes' : 'No unsaved changes'}
            {tabName ? ` - ${tabName}` : ''}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent={{ xs: 'flex-end', sm: 'initial' }}
          spacing={1}
          sx={{
            '& .MuiButton-root': {
              flexShrink: 0,
            },
          }}
        >
          {onDiscard && (
            <Button
              variant="text"
              color="inherit"
              disabled={!canDiscard || isSaving}
              onClick={() => void handleDiscard()}
              sx={CONTENT_ACTION_BUTTON_SX}
            >
              {discardLabel}
            </Button>
          )}
          {children}
        </Stack>
      </Stack>
    </Box>
  );
}
